import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  Text,
  Container,
  Card,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { register } from "../store/slices/authSlice";
import type { RegisterData } from "../types/index";
import { toaster } from "../components/ui/toaster";

export default function RegisterPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState<RegisterData>({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
  });

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[a-z]/.test(password))
      return "Password must contain lowercase letter";
    if (!/[A-Z]/.test(password))
      return "Password must contain uppercase letter";
    if (!/\d/.test(password)) return "Password must contain a number";
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password))
      return "Password must contain a special character";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!/^\S+@\S+$/.test(formData.email)) {
      toaster.create({ title: "Invalid email", type: "error", duration: 3000 });
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      toaster.create({ title: passwordError, type: "error", duration: 3000 });
      return;
    }

    try {
      const result = await dispatch(register(formData)).unwrap();
      toaster.create({
        title: "Registration successful",
        description: result.message || "Check your email for verification code",
        type: "success",
        duration: 5000,
      });
      navigate("/verify-email", { state: { email: formData.email } });
    } catch (error: any) {
      toaster.create({
        title: "Registration failed",
        description: error || "Please try again",
        type: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box
      className="min-h-screen flex items-center justify-center py-12"
      bg="gray.50">
      <Container maxW="2xl">
        <VStack mb={6} textAlign="center">
          <Heading size="xl">Create Account</Heading>
          <Text color="gray.600">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-500 hover:text-blue-600">
              Sign in
            </Link>
          </Text>
        </VStack>

        <Card.Root>
          <Card.Body p={8}>
            <form onSubmit={handleSubmit}>
              <VStack gap={4}>
                <Grid templateColumns="repeat(2, 1fr)" gap={4} width="full">
                  <GridItem>
                    <Field.Root required>
                      <Field.Label>First Name</Field.Label>
                      <Input
                        placeholder="John"
                        value={formData.first_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            first_name: e.target.value,
                          })
                        }
                        required
                      />
                    </Field.Root>
                  </GridItem>
                  <GridItem>
                    <Field.Root required>
                      <Field.Label>Last Name</Field.Label>
                      <Input
                        placeholder="Doe"
                        value={formData.last_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            last_name: e.target.value,
                          })
                        }
                        required
                      />
                    </Field.Root>
                  </GridItem>
                </Grid>

                <Field.Root required width="full">
                  <Field.Label>Email</Field.Label>
                  <Input
                    type="email"
                    placeholder="you@pharmasee.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </Field.Root>

                <Field.Root required width="full">
                  <Field.Label>Password</Field.Label>
                  <Input
                    type="password"
                    placeholder="Your password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required
                  />
                </Field.Root>

                <Field.Root width="full">
                  <Field.Label>Phone</Field.Label>
                  <Input
                    placeholder="+234 xxx xxxx xxx"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </Field.Root>

                <Field.Root width="full">
                  <Field.Label>Address</Field.Label>
                  <Input
                    placeholder="123 Main Street"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                  />
                </Field.Root>

                <Grid templateColumns="repeat(2, 1fr)" gap={4} width="full">
                  <GridItem>
                    <Field.Root>
                      <Field.Label>City</Field.Label>
                      <Input
                        placeholder="Lagos"
                        value={formData.city}
                        onChange={(e) =>
                          setFormData({ ...formData, city: e.target.value })
                        }
                      />
                    </Field.Root>
                  </GridItem>
                  <GridItem>
                    <Field.Root>
                      <Field.Label>State</Field.Label>
                      <Input
                        placeholder="Lagos"
                        value={formData.state}
                        onChange={(e) =>
                          setFormData({ ...formData, state: e.target.value })
                        }
                      />
                    </Field.Root>
                  </GridItem>
                </Grid>

                <Field.Root width="full">
                  <Field.Label>Country</Field.Label>
                  <Input
                    placeholder="Nigeria"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                  />
                </Field.Root>

                <Button
                  type="submit"
                  colorScheme="blue"
                  width="full"
                  loading={loading}
                  mt={4}>
                  Register
                </Button>
              </VStack>
            </form>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
}
